/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Client Portal API
 * Self-service API for clients accessing the Scrivio Client Portal.  ## Overview These endpoints allow authenticated clients to view their profile and manage their portal experience. All endpoints require a valid Firebase token with `userType: \"client\"` claim.  ## Security All endpoints validate: - Firebase authentication - `userType: \"client\"` claim - Client can only access their own data (via `clientId` claim) - Organization scoping (via `organizationId` claim)  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * An answer submission for a question
 */
export interface AnswerSubmission {
    /**
     * Question UUID to answer
     */
    'questionId': string;
    /**
     * The answer value. Type depends on question type: - TEXT_SHORT, TEXT_LONG: string - YES_NO, AGREEMENT: boolean - SINGLE_CHOICE: an option key, or `{key, text}` when the option picked   is marked `allowsFreeText` — the key it names plus the text typed into it - MULTI_CHOICE: array of option keys, at most one of which may be the   `{key, text}` object above - DATE: string (ISO 8601 date) - NUMBER: number - TEXT_LIST: array of non-blank strings, in order, at most the question\'s   `fieldCount` of them 
     */
    'value': any;
}
/**
 * Explicit classification of a cancellation. Ignored on this portal endpoint: a client cannot classify their own cancellation, so the status is always derived from the cancellation timing. The property exists because this schema shares its generated model with the behandelaar API (`openapi-appointments.yaml`). - CANCELLED: Cancelled (generic, e.g. at or after the start time) - CANCELLED_ADVANCE: Timely cancellation (at least the organisation\'s late-cancellation window (default 24 hours) before start) - CANCELLED_LATE: Late cancellation (within the organisation\'s late-cancellation window (default 24 hours) of the start) 
 */

export const CancellationStatus = {
    Cancelled: 'CANCELLED',
    CancelledAdvance: 'CANCELLED_ADVANCE',
    CancelledLate: 'CANCELLED_LATE'
} as const;

export type CancellationStatus = typeof CancellationStatus[keyof typeof CancellationStatus];


/**
 * The language the client reads, which the portal picks its locale from. Defined identically in openapi-clients.yaml.
 */

export const ClientLanguage = {
    Nl: 'NL',
    En: 'EN'
} as const;

export type ClientLanguage = typeof ClientLanguage[keyof typeof ClientLanguage];


/**
 * Client profile information for the portal
 */
export interface ClientProfileResponse {
    /**
     * The client\'s unique identifier
     */
    'id': string;
    /**
     * Client\'s first name
     */
    'firstName': string;
    /**
     * Client\'s last name
     */
    'lastName': string;
    /**
     * Client\'s initials
     */
    'initials'?: string | null;
    /**
     * Name prefix (e.g., \"van\", \"de\")
     */
    'prefix'?: string | null;
    /**
     * Client\'s email address
     */
    'email'?: string | null;
    /**
     * Client\'s phone number
     */
    'phone'?: string | null;
    /**
     * Name of the organization the client belongs to
     */
    'organizationName': string;
    'language': ClientLanguage;
}


/**
 * Response containing a signed download URL
 */
export interface DocumentDownloadResponse {
    /**
     * Signed URL for downloading the document
     */
    'downloadUrl': string;
    /**
     * Timestamp when the URL expires (epoch milliseconds)
     */
    'expiresAt': number;
}
/**
 * Confirmation of saved draft
 */
export interface DraftSavedResponse {
    /**
     * Draft save timestamp (epoch milliseconds)
     */
    'savedAt': number;
    /**
     * Confirmation message
     */
    'message'?: string;
}
/**
 * Confirmation of successful form submission
 */
export interface FormSubmissionConfirmation {
    /**
     * The created submission UUID
     */
    'submissionId': string;
    /**
     * Submission timestamp (epoch milliseconds)
     */
    'submittedAt': number;
    /**
     * Confirmation message
     */
    'message'?: string;
}
/**
 * An attendee of the appointment
 */
export interface PortalAppointmentAttendee {
    /**
     * Name of the attendee
     */
    'name': string;
}
/**
 * Detailed information about an appointment
 */
export interface PortalAppointmentDetail {
    /**
     * The appointment\'s unique identifier
     */
    'id': string;
    /**
     * Title of the appointment
     */
    'title': string;
    /**
     * Start time of the appointment (ISO 8601)
     */
    'startTime': string;
    /**
     * End time of the appointment (ISO 8601)
     */
    'endTime': string;
    'locationType': PortalLocationType;
    'status': PortalAppointmentStatus;
    /**
     * Name of the behandelaar organizing the appointment
     */
    'organizerName': string;
    /**
     * Additional description or notes about the appointment
     */
    'description'?: string | null;
    /**
     * Room name or address for physical appointments
     */
    'locationDetails'?: string | null;
    /**
     * URL for online appointments
     */
    'onlineUrl'?: string | null;
    /**
     * Other participants in the appointment (names only)
     */
    'attendees': Array<PortalAppointmentAttendee>;
}


/**
 * Paginated list of appointments
 */
export interface PortalAppointmentListResponse {
    /**
     * List of appointments for current page
     */
    'content': Array<PortalAppointmentSummary>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of appointments
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Appointment status: - SCHEDULED: Appointment is scheduled - CANCELLED: Appointment was cancelled - CANCELLED_ADVANCE: Cancelled with advance notice - CANCELLED_LATE: Cancelled with late/short notice - NO_SHOW: Client didn\'t show up - COMPLETED: Appointment completed 
 */

export const PortalAppointmentStatus = {
    Scheduled: 'SCHEDULED',
    Cancelled: 'CANCELLED',
    CancelledAdvance: 'CANCELLED_ADVANCE',
    CancelledLate: 'CANCELLED_LATE',
    NoShow: 'NO_SHOW',
    Completed: 'COMPLETED'
} as const;

export type PortalAppointmentStatus = typeof PortalAppointmentStatus[keyof typeof PortalAppointmentStatus];


/**
 * Summary of an appointment for listing
 */
export interface PortalAppointmentSummary {
    /**
     * The appointment\'s unique identifier
     */
    'id': string;
    /**
     * Title of the appointment
     */
    'title': string;
    /**
     * Start time of the appointment (ISO 8601)
     */
    'startTime': string;
    /**
     * End time of the appointment (ISO 8601)
     */
    'endTime': string;
    'locationType': PortalLocationType;
    'status': PortalAppointmentStatus;
    /**
     * Name of the behandelaar organizing the appointment
     */
    'organizerName': string;
}


/**
 * Request to cancel an appointment from the client portal.  Named apart from the behandelaar-side `CancelAppointmentRequest` on purpose: both specs generate into `nl.scrivio.dto`, so a shared name means one class, and this spec is generated last — a field the behandelaar endpoint needs would be silently discarded (#3222). The behandelaar\'s replacement question (#3438) is exactly such a field, and it has no meaning for a cliënt cancelling their own afspraak. 
 */
export interface PortalCancelAppointmentRequest {
    /**
     * Optional reason for cancellation
     */
    'reason'?: string | null;
    'cancellationStatus'?: CancellationStatus;
}


/**
 * Paginated list of exposed documents
 */
export interface PortalDocumentListResponse {
    /**
     * List of documents for current page
     */
    'content': Array<PortalDocumentResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of exposed documents
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Document information for the client portal
 */
export interface PortalDocumentResponse {
    /**
     * The document\'s unique identifier
     */
    'id': string;
    /**
     * Human-readable name given to the document by the practice, or null when it has none of its own. The portal shows `displayName` when set and `filename` otherwise. Carries no file extension. 
     */
    'displayName'?: string | null;
    /**
     * Original filename, as uploaded. Also the name a download is saved under.
     */
    'filename': string;
    /**
     * MIME type of the file
     */
    'contentType': string;
    /**
     * File size in bytes
     */
    'size': number;
    /**
     * Timestamp when the document was exposed (epoch milliseconds)
     */
    'exposedAt': number;
    /**
     * Whether the client must explicitly acknowledge this document. When true and `acknowledgedAt` is null, the portal UI shows the agree footer. 
     */
    'requiresAcknowledgment': boolean;
    /**
     * Timestamp when the client acknowledged this document (epoch milliseconds). Null when the document has not yet been acknowledged. 
     */
    'acknowledgedAt'?: number | null;
}
/**
 * A saved draft answer
 */
export interface PortalDraftAnswer {
    /**
     * Question UUID
     */
    'questionId': string;
    /**
     * The saved answer value
     */
    'value': any;
}
/**
 * Status of a form assignment: - PENDING: Form has not been completed yet - COMPLETED: Client has submitted the form 
 */

export const PortalFormAssignmentStatus = {
    Pending: 'PENDING',
    Completed: 'COMPLETED'
} as const;

export type PortalFormAssignmentStatus = typeof PortalFormAssignmentStatus[keyof typeof PortalFormAssignmentStatus];


/**
 * Form with all questions for filling
 */
export interface PortalFormDetailResponse {
    /**
     * Form assignment UUID
     */
    'assignmentId': string;
    /**
     * Form UUID
     */
    'formId': string;
    /**
     * Title of the form
     */
    'formTitle': string;
    /**
     * Description of the form
     */
    'formDescription'?: string | null;
    'status': PortalFormAssignmentStatus;
    /**
     * Form items in order
     */
    'items': Array<PortalFormItem>;
    /**
     * Previously saved draft answers (if any)
     */
    'draftAnswers'?: Array<PortalDraftAnswer> | null;
    /**
     * Assignment timestamp (epoch milliseconds)
     */
    'assignedAt': number;
    /**
     * Due date for completion (epoch milliseconds)
     */
    'dueDate'?: number | null;
    /**
     * Completion timestamp (epoch milliseconds)
     */
    'completedAt'?: number | null;
}


/**
 * A form item (question, static text, or document link)
 */
export interface PortalFormItem {
    /**
     * Form item UUID
     */
    'id': string;
    /**
     * Position of the item in the form (1-based ordering)
     */
    'position': number;
    'itemType': PortalFormItemType;
    'question'?: PortalFormQuestion;
    'content'?: PortalFormItemContent;
}


/**
 * Content for STATIC_TEXT or DOCUMENT_LINK form items
 */
export interface PortalFormItemContent {
    /**
     * Text content for STATIC_TEXT items
     */
    'text'?: string;
    /**
     * Title for DOCUMENT_LINK items
     */
    'title'?: string;
    /**
     * URL for DOCUMENT_LINK items
     */
    'url'?: string;
}
/**
 * Type of form item
 */

export const PortalFormItemType = {
    Question: 'QUESTION',
    StaticText: 'STATIC_TEXT',
    DocumentLink: 'DOCUMENT_LINK'
} as const;

export type PortalFormItemType = typeof PortalFormItemType[keyof typeof PortalFormItemType];


/**
 * List of assigned forms
 */
export interface PortalFormListResponse {
    'forms': Array<PortalFormSummary>;
    /**
     * Total number of assigned forms
     */
    'totalCount': number;
}
/**
 * Question details for filling
 */
export interface PortalFormQuestion {
    /**
     * Question UUID
     */
    'id': string;
    /**
     * Human-readable key for the question (e.g., \'client_name\')
     */
    'key': string;
    'type': PortalQuestionType;
    /**
     * The question text
     */
    'text': string;
    /**
     * Additional description or instructions
     */
    'description'?: string | null;
    /**
     * Options for choice-type questions
     */
    'options'?: Array<PortalQuestionOption> | null;
    /**
     * How many single-line fields a TEXT_LIST question shows; its answer is an array of at most this many non-blank strings. Null for every other type. 
     */
    'fieldCount'?: number | null;
    /**
     * Whether an answer is required
     */
    'required': boolean;
}


/**
 * Summary of an assigned form for listing
 */
export interface PortalFormSummary {
    /**
     * Form assignment UUID
     */
    'assignmentId': string;
    /**
     * Form UUID
     */
    'formId': string;
    /**
     * Title of the form
     */
    'formTitle': string;
    /**
     * Description of the form
     */
    'formDescription'?: string | null;
    'status': PortalFormAssignmentStatus;
    /**
     * Number of questions in the form
     */
    'questionCount': number;
    /**
     * Assignment timestamp (epoch milliseconds)
     */
    'assignedAt': number;
    /**
     * Due date for completion (epoch milliseconds)
     */
    'dueDate'?: number | null;
    /**
     * Completion timestamp (epoch milliseconds)
     */
    'completedAt'?: number | null;
    /**
     * Whether the form is past its due date
     */
    'isOverdue'?: boolean;
}


/**
 * Paginated list of exposed invoices
 */
export interface PortalInvoiceListResponse {
    /**
     * List of invoices for current page
     */
    'content': Array<PortalInvoiceResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of exposed invoices
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Invoice information for the client portal
 */
export interface PortalInvoiceResponse {
    /**
     * The invoice\'s unique identifier
     */
    'id': string;
    /**
     * Invoice number (e.g. \"2026-0001\")
     */
    'invoiceNumber'?: string | null;
    /**
     * Invoice date
     */
    'invoiceDate': string;
    /**
     * Total amount including BTW
     */
    'totalEuro': number;
    /**
     * Payment status of the invoice
     */
    'status': PortalInvoiceResponseStatusEnum;
    /**
     * Timestamp when the invoice\'s stored PDF was exposed to the client in the portal (epoch milliseconds) 
     */
    'visibleAt'?: number | null;
    /**
     * The practice\'s betaallink (an absolute https URL), or null. Only an invoice that still awaits payment (SENT/OVERDUE) carries one; a paid invoice never does. 
     */
    'paymentLink'?: string | null;
}

export const PortalInvoiceResponseStatusEnum = {
    Sent: 'SENT',
    Paid: 'PAID',
    Overdue: 'OVERDUE'
} as const;

export type PortalInvoiceResponseStatusEnum = typeof PortalInvoiceResponseStatusEnum[keyof typeof PortalInvoiceResponseStatusEnum];

/**
 * Location type: - PHYSICAL: In-person meeting - ONLINE: Video call/online meeting - NOT_APPLICABLE: Location not applicable 
 */

export const PortalLocationType = {
    Physical: 'PHYSICAL',
    Online: 'ONLINE',
    NotApplicable: 'NOT_APPLICABLE'
} as const;

export type PortalLocationType = typeof PortalLocationType[keyof typeof PortalLocationType];


/**
 * Option for choice-type questions
 */
export interface PortalQuestionOption {
    /**
     * Unique identifier for this option
     */
    'key': string;
    /**
     * Display text for this option
     */
    'label': string;
    /**
     * Whether picking this option also accepts typed text — an \"Anders, namelijk\". At most one option per question carries it. An answer naming such an option submits `{key, text}` instead of the bare key; see `AnswerSubmission.value`. 
     */
    'allowsFreeText'?: boolean;
}
/**
 * Type of question
 */

export const PortalQuestionType = {
    TextShort: 'TEXT_SHORT',
    TextLong: 'TEXT_LONG',
    YesNo: 'YES_NO',
    SingleChoice: 'SINGLE_CHOICE',
    MultiChoice: 'MULTI_CHOICE',
    Agreement: 'AGREEMENT',
    Date: 'DATE',
    Number: 'NUMBER',
    TextList: 'TEXT_LIST'
} as const;

export type PortalQuestionType = typeof PortalQuestionType[keyof typeof PortalQuestionType];


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Request to save form draft
 */
export interface SaveDraftRequest {
    /**
     * List of draft answers to save
     */
    'answers': Array<AnswerSubmission>;
}
/**
 * Request to submit form answers
 */
export interface SubmitFormAnswersRequest {
    /**
     * List of answers to submit
     */
    'answers': Array<AnswerSubmission>;
}

/**
 * PortalApi - axios parameter creator
 */
export const PortalApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the authenticated client\'s profile information.  This endpoint extracts the `clientId` from the Firebase token claims and returns the corresponding client\'s profile data.  **Note:** Sensitive fields (BSN, full address) are not included in the response. 
         * @summary Get current client profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentClientProfile: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/portal/me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves the logo for the client\'s organization. Streams the image bytes with the correct Content-Type header.  If the organization has no logo configured, returns 404. 
         * @summary Get organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPortalOrganizationLogo: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/portal/organization/logo`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'image/png,image/jpeg,image/svg+xml,application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PortalApi - functional programming interface
 */
export const PortalApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PortalApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the authenticated client\'s profile information.  This endpoint extracts the `clientId` from the Firebase token claims and returns the corresponding client\'s profile data.  **Note:** Sensitive fields (BSN, full address) are not included in the response. 
         * @summary Get current client profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCurrentClientProfile(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientProfileResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCurrentClientProfile(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalApi.getCurrentClientProfile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves the logo for the client\'s organization. Streams the image bytes with the correct Content-Type header.  If the organization has no logo configured, returns 404. 
         * @summary Get organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPortalOrganizationLogo(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPortalOrganizationLogo(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalApi.getPortalOrganizationLogo']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * PortalApi - factory interface
 */
export const PortalApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PortalApiFp(configuration)
    return {
        /**
         * Returns the authenticated client\'s profile information.  This endpoint extracts the `clientId` from the Firebase token claims and returns the corresponding client\'s profile data.  **Note:** Sensitive fields (BSN, full address) are not included in the response. 
         * @summary Get current client profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentClientProfile(options?: RawAxiosRequestConfig): AxiosPromise<ClientProfileResponse> {
            return localVarFp.getCurrentClientProfile(options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves the logo for the client\'s organization. Streams the image bytes with the correct Content-Type header.  If the organization has no logo configured, returns 404. 
         * @summary Get organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPortalOrganizationLogo(options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.getPortalOrganizationLogo(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PortalApi - object-oriented interface
 */
export class PortalApi extends BaseAPI {
    /**
     * Returns the authenticated client\'s profile information.  This endpoint extracts the `clientId` from the Firebase token claims and returns the corresponding client\'s profile data.  **Note:** Sensitive fields (BSN, full address) are not included in the response. 
     * @summary Get current client profile
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCurrentClientProfile(options?: RawAxiosRequestConfig) {
        return PortalApiFp(this.configuration).getCurrentClientProfile(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves the logo for the client\'s organization. Streams the image bytes with the correct Content-Type header.  If the organization has no logo configured, returns 404. 
     * @summary Get organization logo
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getPortalOrganizationLogo(options?: RawAxiosRequestConfig) {
        return PortalApiFp(this.configuration).getPortalOrganizationLogo(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * PortalAppointmentsApi - axios parameter creator
 */
export const PortalAppointmentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Cancels the specified appointment.  Changes the appointment status to a cancelled state and records the cancellation timestamp.  **Cancellation type is determined by timing**, against the organisation\'s late-cancellation window (default 24 hours): - at least the window before start: CANCELLED_ADVANCE - inside the window: CANCELLED_LATE  **Restrictions:** - Only SCHEDULED appointments can be cancelled - Cannot cancel appointments that have already started or are in the past - Cannot cancel already cancelled or completed appointments 
         * @summary Cancel appointment
         * @param {string} appointmentId The appointment\&#39;s unique identifier
         * @param {PortalCancelAppointmentRequest} [portalCancelAppointmentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelPortalAppointment: async (appointmentId: string, portalCancelAppointmentRequest?: PortalCancelAppointmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('cancelPortalAppointment', 'appointmentId', appointmentId)
            const localVarPath = `/api/portal/appointments/{appointmentId}/cancel`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portalCancelAppointmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns detailed information about a specific appointment.  The client must be a participant of the appointment to access its details.  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
         * @summary Get appointment details
         * @param {string} appointmentId The appointment\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPortalAppointment: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getPortalAppointment', 'appointmentId', appointmentId)
            const localVarPath = `/api/portal/appointments/{appointmentId}`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of appointments for the authenticated client.  Supports filtering by date range and status. Appointments are ordered by startTime descending (most recent first).  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
         * @summary List appointments
         * @param {string} [from] Start of date range (inclusive)
         * @param {string} [to] End of date range (inclusive)
         * @param {PortalAppointmentStatus} [status] Filter by appointment status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPortalAppointments: async (from?: string, to?: string, status?: PortalAppointmentStatus, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/portal/appointments`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (from !== undefined) {
                localVarQueryParameter['from'] = (from as any instanceof Date) ?
                    (from as any).toISOString().substring(0,10) :
                    from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = (to as any instanceof Date) ?
                    (to as any).toISOString().substring(0,10) :
                    to;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PortalAppointmentsApi - functional programming interface
 */
export const PortalAppointmentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PortalAppointmentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Cancels the specified appointment.  Changes the appointment status to a cancelled state and records the cancellation timestamp.  **Cancellation type is determined by timing**, against the organisation\'s late-cancellation window (default 24 hours): - at least the window before start: CANCELLED_ADVANCE - inside the window: CANCELLED_LATE  **Restrictions:** - Only SCHEDULED appointments can be cancelled - Cannot cancel appointments that have already started or are in the past - Cannot cancel already cancelled or completed appointments 
         * @summary Cancel appointment
         * @param {string} appointmentId The appointment\&#39;s unique identifier
         * @param {PortalCancelAppointmentRequest} [portalCancelAppointmentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelPortalAppointment(appointmentId: string, portalCancelAppointmentRequest?: PortalCancelAppointmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalAppointmentSummary>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelPortalAppointment(appointmentId, portalCancelAppointmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalAppointmentsApi.cancelPortalAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns detailed information about a specific appointment.  The client must be a participant of the appointment to access its details.  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
         * @summary Get appointment details
         * @param {string} appointmentId The appointment\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPortalAppointment(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalAppointmentDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPortalAppointment(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalAppointmentsApi.getPortalAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of appointments for the authenticated client.  Supports filtering by date range and status. Appointments are ordered by startTime descending (most recent first).  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
         * @summary List appointments
         * @param {string} [from] Start of date range (inclusive)
         * @param {string} [to] End of date range (inclusive)
         * @param {PortalAppointmentStatus} [status] Filter by appointment status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listPortalAppointments(from?: string, to?: string, status?: PortalAppointmentStatus, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalAppointmentListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listPortalAppointments(from, to, status, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalAppointmentsApi.listPortalAppointments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * PortalAppointmentsApi - factory interface
 */
export const PortalAppointmentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PortalAppointmentsApiFp(configuration)
    return {
        /**
         * Cancels the specified appointment.  Changes the appointment status to a cancelled state and records the cancellation timestamp.  **Cancellation type is determined by timing**, against the organisation\'s late-cancellation window (default 24 hours): - at least the window before start: CANCELLED_ADVANCE - inside the window: CANCELLED_LATE  **Restrictions:** - Only SCHEDULED appointments can be cancelled - Cannot cancel appointments that have already started or are in the past - Cannot cancel already cancelled or completed appointments 
         * @summary Cancel appointment
         * @param {string} appointmentId The appointment\&#39;s unique identifier
         * @param {PortalCancelAppointmentRequest} [portalCancelAppointmentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelPortalAppointment(appointmentId: string, portalCancelAppointmentRequest?: PortalCancelAppointmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<PortalAppointmentSummary> {
            return localVarFp.cancelPortalAppointment(appointmentId, portalCancelAppointmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns detailed information about a specific appointment.  The client must be a participant of the appointment to access its details.  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
         * @summary Get appointment details
         * @param {string} appointmentId The appointment\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPortalAppointment(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<PortalAppointmentDetail> {
            return localVarFp.getPortalAppointment(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of appointments for the authenticated client.  Supports filtering by date range and status. Appointments are ordered by startTime descending (most recent first).  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
         * @summary List appointments
         * @param {string} [from] Start of date range (inclusive)
         * @param {string} [to] End of date range (inclusive)
         * @param {PortalAppointmentStatus} [status] Filter by appointment status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPortalAppointments(from?: string, to?: string, status?: PortalAppointmentStatus, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<PortalAppointmentListResponse> {
            return localVarFp.listPortalAppointments(from, to, status, page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PortalAppointmentsApi - object-oriented interface
 */
export class PortalAppointmentsApi extends BaseAPI {
    /**
     * Cancels the specified appointment.  Changes the appointment status to a cancelled state and records the cancellation timestamp.  **Cancellation type is determined by timing**, against the organisation\'s late-cancellation window (default 24 hours): - at least the window before start: CANCELLED_ADVANCE - inside the window: CANCELLED_LATE  **Restrictions:** - Only SCHEDULED appointments can be cancelled - Cannot cancel appointments that have already started or are in the past - Cannot cancel already cancelled or completed appointments 
     * @summary Cancel appointment
     * @param {string} appointmentId The appointment\&#39;s unique identifier
     * @param {PortalCancelAppointmentRequest} [portalCancelAppointmentRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelPortalAppointment(appointmentId: string, portalCancelAppointmentRequest?: PortalCancelAppointmentRequest, options?: RawAxiosRequestConfig) {
        return PortalAppointmentsApiFp(this.configuration).cancelPortalAppointment(appointmentId, portalCancelAppointmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns detailed information about a specific appointment.  The client must be a participant of the appointment to access its details.  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
     * @summary Get appointment details
     * @param {string} appointmentId The appointment\&#39;s unique identifier
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getPortalAppointment(appointmentId: string, options?: RawAxiosRequestConfig) {
        return PortalAppointmentsApiFp(this.configuration).getPortalAppointment(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of appointments for the authenticated client.  Supports filtering by date range and status. Appointments are ordered by startTime descending (most recent first).  **Note:** Sensitive data such as internal notes, billing information, and recording URLs are not included in the response. 
     * @summary List appointments
     * @param {string} [from] Start of date range (inclusive)
     * @param {string} [to] End of date range (inclusive)
     * @param {PortalAppointmentStatus} [status] Filter by appointment status
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listPortalAppointments(from?: string, to?: string, status?: PortalAppointmentStatus, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return PortalAppointmentsApiFp(this.configuration).listPortalAppointments(from, to, status, page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * PortalDocumentsApi - axios parameter creator
 */
export const PortalDocumentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Records the authenticated client\'s explicit acknowledgment of the specified document (\"Ik heb dit document gelezen en ga akkoord\").  The request body is empty — the server captures the client IP (from `X-Forwarded-For` first hop, falling back to the remote address) and the `User-Agent` header (truncated to 512 chars) for the audit trail.  Eligibility is enforced by the domain layer: - The document must be exposed to this client - The document must be flagged as requiring acknowledgment - The document must not already be acknowledged  Cross-client / cross-organization access returns 404 to avoid leaking the existence of documents that don\'t belong to the calling client. 
         * @summary Acknowledge a document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acknowledgePortalDocument: async (documentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('acknowledgePortalDocument', 'documentId', documentId)
            const localVarPath = `/api/portal/documents/{documentId}/acknowledge`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a signed download URL for the specified document. The client must be authenticated and the document must be exposed to them.  The returned URL is time-limited and can be used to download the file directly. 
         * @summary Download exposed document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadExposedDocument: async (documentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('downloadExposedDocument', 'documentId', documentId)
            const localVarPath = `/api/portal/documents/{documentId}/download`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of documents that have been exposed to the authenticated client. Only documents where `exposedToClient = true` are returned.  Documents are ordered by `exposedAt` descending (most recently exposed first). 
         * @summary List exposed documents
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listExposedDocuments: async (page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/portal/documents`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PortalDocumentsApi - functional programming interface
 */
export const PortalDocumentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PortalDocumentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Records the authenticated client\'s explicit acknowledgment of the specified document (\"Ik heb dit document gelezen en ga akkoord\").  The request body is empty — the server captures the client IP (from `X-Forwarded-For` first hop, falling back to the remote address) and the `User-Agent` header (truncated to 512 chars) for the audit trail.  Eligibility is enforced by the domain layer: - The document must be exposed to this client - The document must be flagged as requiring acknowledgment - The document must not already be acknowledged  Cross-client / cross-organization access returns 404 to avoid leaking the existence of documents that don\'t belong to the calling client. 
         * @summary Acknowledge a document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acknowledgePortalDocument(documentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalDocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acknowledgePortalDocument(documentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalDocumentsApi.acknowledgePortalDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a signed download URL for the specified document. The client must be authenticated and the document must be exposed to them.  The returned URL is time-limited and can be used to download the file directly. 
         * @summary Download exposed document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadExposedDocument(documentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentDownloadResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadExposedDocument(documentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalDocumentsApi.downloadExposedDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of documents that have been exposed to the authenticated client. Only documents where `exposedToClient = true` are returned.  Documents are ordered by `exposedAt` descending (most recently exposed first). 
         * @summary List exposed documents
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listExposedDocuments(page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalDocumentListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listExposedDocuments(page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalDocumentsApi.listExposedDocuments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * PortalDocumentsApi - factory interface
 */
export const PortalDocumentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PortalDocumentsApiFp(configuration)
    return {
        /**
         * Records the authenticated client\'s explicit acknowledgment of the specified document (\"Ik heb dit document gelezen en ga akkoord\").  The request body is empty — the server captures the client IP (from `X-Forwarded-For` first hop, falling back to the remote address) and the `User-Agent` header (truncated to 512 chars) for the audit trail.  Eligibility is enforced by the domain layer: - The document must be exposed to this client - The document must be flagged as requiring acknowledgment - The document must not already be acknowledged  Cross-client / cross-organization access returns 404 to avoid leaking the existence of documents that don\'t belong to the calling client. 
         * @summary Acknowledge a document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acknowledgePortalDocument(documentId: string, options?: RawAxiosRequestConfig): AxiosPromise<PortalDocumentResponse> {
            return localVarFp.acknowledgePortalDocument(documentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a signed download URL for the specified document. The client must be authenticated and the document must be exposed to them.  The returned URL is time-limited and can be used to download the file directly. 
         * @summary Download exposed document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadExposedDocument(documentId: string, options?: RawAxiosRequestConfig): AxiosPromise<DocumentDownloadResponse> {
            return localVarFp.downloadExposedDocument(documentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of documents that have been exposed to the authenticated client. Only documents where `exposedToClient = true` are returned.  Documents are ordered by `exposedAt` descending (most recently exposed first). 
         * @summary List exposed documents
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listExposedDocuments(page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<PortalDocumentListResponse> {
            return localVarFp.listExposedDocuments(page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PortalDocumentsApi - object-oriented interface
 */
export class PortalDocumentsApi extends BaseAPI {
    /**
     * Records the authenticated client\'s explicit acknowledgment of the specified document (\"Ik heb dit document gelezen en ga akkoord\").  The request body is empty — the server captures the client IP (from `X-Forwarded-For` first hop, falling back to the remote address) and the `User-Agent` header (truncated to 512 chars) for the audit trail.  Eligibility is enforced by the domain layer: - The document must be exposed to this client - The document must be flagged as requiring acknowledgment - The document must not already be acknowledged  Cross-client / cross-organization access returns 404 to avoid leaking the existence of documents that don\'t belong to the calling client. 
     * @summary Acknowledge a document
     * @param {string} documentId The document\&#39;s unique identifier
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public acknowledgePortalDocument(documentId: string, options?: RawAxiosRequestConfig) {
        return PortalDocumentsApiFp(this.configuration).acknowledgePortalDocument(documentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a signed download URL for the specified document. The client must be authenticated and the document must be exposed to them.  The returned URL is time-limited and can be used to download the file directly. 
     * @summary Download exposed document
     * @param {string} documentId The document\&#39;s unique identifier
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadExposedDocument(documentId: string, options?: RawAxiosRequestConfig) {
        return PortalDocumentsApiFp(this.configuration).downloadExposedDocument(documentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of documents that have been exposed to the authenticated client. Only documents where `exposedToClient = true` are returned.  Documents are ordered by `exposedAt` descending (most recently exposed first). 
     * @summary List exposed documents
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listExposedDocuments(page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return PortalDocumentsApiFp(this.configuration).listExposedDocuments(page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * PortalFormsApi - axios parameter creator
 */
export const PortalFormsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the assigned form with all questions (latest versions). Includes existing answers if the form has been partially saved as draft. 
         * @summary Get form for filling
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormForFilling: async (assignmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'assignmentId' is not null or undefined
            assertParamExists('getFormForFilling', 'assignmentId', assignmentId)
            const localVarPath = `/api/portal/forms/{assignmentId}`
                .replace(`{${"assignmentId"}}`, encodeURIComponent(String(assignmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a list of forms assigned to the authenticated client. Includes form title, due date, status, and question count. 
         * @summary List my assigned forms
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAssignedForms: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/portal/forms`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Saves the client\'s answers as a draft without completing the form. Can be retrieved on the next GET request for the form. Overwrites any previously saved draft. 
         * @summary Save form draft
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {SaveDraftRequest} saveDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveFormDraft: async (assignmentId: string, saveDraftRequest: SaveDraftRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'assignmentId' is not null or undefined
            assertParamExists('saveFormDraft', 'assignmentId', assignmentId)
            // verify required parameter 'saveDraftRequest' is not null or undefined
            assertParamExists('saveFormDraft', 'saveDraftRequest', saveDraftRequest)
            const localVarPath = `/api/portal/forms/{assignmentId}/draft`
                .replace(`{${"assignmentId"}}`, encodeURIComponent(String(assignmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(saveDraftRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Submits the client\'s answers for the assigned form. All required questions must be answered. Creates a FormSubmission and Answer records, and marks the assignment as completed. 
         * @summary Submit form answers
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {SubmitFormAnswersRequest} submitFormAnswersRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitFormAnswers: async (assignmentId: string, submitFormAnswersRequest: SubmitFormAnswersRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'assignmentId' is not null or undefined
            assertParamExists('submitFormAnswers', 'assignmentId', assignmentId)
            // verify required parameter 'submitFormAnswersRequest' is not null or undefined
            assertParamExists('submitFormAnswers', 'submitFormAnswersRequest', submitFormAnswersRequest)
            const localVarPath = `/api/portal/forms/{assignmentId}/submit`
                .replace(`{${"assignmentId"}}`, encodeURIComponent(String(assignmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(submitFormAnswersRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PortalFormsApi - functional programming interface
 */
export const PortalFormsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PortalFormsApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the assigned form with all questions (latest versions). Includes existing answers if the form has been partially saved as draft. 
         * @summary Get form for filling
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFormForFilling(assignmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalFormDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFormForFilling(assignmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalFormsApi.getFormForFilling']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a list of forms assigned to the authenticated client. Includes form title, due date, status, and question count. 
         * @summary List my assigned forms
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyAssignedForms(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalFormListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyAssignedForms(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalFormsApi.listMyAssignedForms']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Saves the client\'s answers as a draft without completing the form. Can be retrieved on the next GET request for the form. Overwrites any previously saved draft. 
         * @summary Save form draft
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {SaveDraftRequest} saveDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async saveFormDraft(assignmentId: string, saveDraftRequest: SaveDraftRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DraftSavedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.saveFormDraft(assignmentId, saveDraftRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalFormsApi.saveFormDraft']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Submits the client\'s answers for the assigned form. All required questions must be answered. Creates a FormSubmission and Answer records, and marks the assignment as completed. 
         * @summary Submit form answers
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {SubmitFormAnswersRequest} submitFormAnswersRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async submitFormAnswers(assignmentId: string, submitFormAnswersRequest: SubmitFormAnswersRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionConfirmation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.submitFormAnswers(assignmentId, submitFormAnswersRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalFormsApi.submitFormAnswers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * PortalFormsApi - factory interface
 */
export const PortalFormsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PortalFormsApiFp(configuration)
    return {
        /**
         * Returns the assigned form with all questions (latest versions). Includes existing answers if the form has been partially saved as draft. 
         * @summary Get form for filling
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormForFilling(assignmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<PortalFormDetailResponse> {
            return localVarFp.getFormForFilling(assignmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a list of forms assigned to the authenticated client. Includes form title, due date, status, and question count. 
         * @summary List my assigned forms
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAssignedForms(options?: RawAxiosRequestConfig): AxiosPromise<PortalFormListResponse> {
            return localVarFp.listMyAssignedForms(options).then((request) => request(axios, basePath));
        },
        /**
         * Saves the client\'s answers as a draft without completing the form. Can be retrieved on the next GET request for the form. Overwrites any previously saved draft. 
         * @summary Save form draft
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {SaveDraftRequest} saveDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveFormDraft(assignmentId: string, saveDraftRequest: SaveDraftRequest, options?: RawAxiosRequestConfig): AxiosPromise<DraftSavedResponse> {
            return localVarFp.saveFormDraft(assignmentId, saveDraftRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Submits the client\'s answers for the assigned form. All required questions must be answered. Creates a FormSubmission and Answer records, and marks the assignment as completed. 
         * @summary Submit form answers
         * @param {string} assignmentId The form assignment\&#39;s unique identifier
         * @param {SubmitFormAnswersRequest} submitFormAnswersRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitFormAnswers(assignmentId: string, submitFormAnswersRequest: SubmitFormAnswersRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionConfirmation> {
            return localVarFp.submitFormAnswers(assignmentId, submitFormAnswersRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PortalFormsApi - object-oriented interface
 */
export class PortalFormsApi extends BaseAPI {
    /**
     * Returns the assigned form with all questions (latest versions). Includes existing answers if the form has been partially saved as draft. 
     * @summary Get form for filling
     * @param {string} assignmentId The form assignment\&#39;s unique identifier
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFormForFilling(assignmentId: string, options?: RawAxiosRequestConfig) {
        return PortalFormsApiFp(this.configuration).getFormForFilling(assignmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a list of forms assigned to the authenticated client. Includes form title, due date, status, and question count. 
     * @summary List my assigned forms
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyAssignedForms(options?: RawAxiosRequestConfig) {
        return PortalFormsApiFp(this.configuration).listMyAssignedForms(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Saves the client\'s answers as a draft without completing the form. Can be retrieved on the next GET request for the form. Overwrites any previously saved draft. 
     * @summary Save form draft
     * @param {string} assignmentId The form assignment\&#39;s unique identifier
     * @param {SaveDraftRequest} saveDraftRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public saveFormDraft(assignmentId: string, saveDraftRequest: SaveDraftRequest, options?: RawAxiosRequestConfig) {
        return PortalFormsApiFp(this.configuration).saveFormDraft(assignmentId, saveDraftRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Submits the client\'s answers for the assigned form. All required questions must be answered. Creates a FormSubmission and Answer records, and marks the assignment as completed. 
     * @summary Submit form answers
     * @param {string} assignmentId The form assignment\&#39;s unique identifier
     * @param {SubmitFormAnswersRequest} submitFormAnswersRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public submitFormAnswers(assignmentId: string, submitFormAnswersRequest: SubmitFormAnswersRequest, options?: RawAxiosRequestConfig) {
        return PortalFormsApiFp(this.configuration).submitFormAnswers(assignmentId, submitFormAnswersRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * PortalInvoicesApi - axios parameter creator
 */
export const PortalInvoicesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Streams the stored PDF of an invoice that has been exposed to the authenticated client — the document they were sent, not a fresh render.  Cross-client / non-exposed access returns 404 to avoid leaking the existence of invoices that don\'t belong to the calling client. An invoice that is not issued, or has no stored document, returns 404 for the same reason. 
         * @summary Download exposed invoice PDF
         * @param {string} invoiceId The invoice\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadPortalInvoicePdf: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('downloadPortalInvoicePdf', 'invoiceId', invoiceId)
            const localVarPath = `/api/portal/invoices/{invoiceId}/pdf`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/pdf,application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of invoices that a practitioner has exposed to the authenticated client in the portal. Invoices are ordered by invoice date descending (most recent first).  An invoice is exposed by exposing its stored PDF in the client\'s dossier, so an invoice with no stored document is never listed. Only issued invoices are returned; cancelled (credited) ones leave the list by the same rule. 
         * @summary List exposed invoices
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPortalInvoices: async (page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/portal/invoices`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PortalInvoicesApi - functional programming interface
 */
export const PortalInvoicesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PortalInvoicesApiAxiosParamCreator(configuration)
    return {
        /**
         * Streams the stored PDF of an invoice that has been exposed to the authenticated client — the document they were sent, not a fresh render.  Cross-client / non-exposed access returns 404 to avoid leaking the existence of invoices that don\'t belong to the calling client. An invoice that is not issued, or has no stored document, returns 404 for the same reason. 
         * @summary Download exposed invoice PDF
         * @param {string} invoiceId The invoice\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadPortalInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadPortalInvoicePdf(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalInvoicesApi.downloadPortalInvoicePdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of invoices that a practitioner has exposed to the authenticated client in the portal. Invoices are ordered by invoice date descending (most recent first).  An invoice is exposed by exposing its stored PDF in the client\'s dossier, so an invoice with no stored document is never listed. Only issued invoices are returned; cancelled (credited) ones leave the list by the same rule. 
         * @summary List exposed invoices
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listPortalInvoices(page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortalInvoiceListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listPortalInvoices(page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PortalInvoicesApi.listPortalInvoices']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * PortalInvoicesApi - factory interface
 */
export const PortalInvoicesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PortalInvoicesApiFp(configuration)
    return {
        /**
         * Streams the stored PDF of an invoice that has been exposed to the authenticated client — the document they were sent, not a fresh render.  Cross-client / non-exposed access returns 404 to avoid leaking the existence of invoices that don\'t belong to the calling client. An invoice that is not issued, or has no stored document, returns 404 for the same reason. 
         * @summary Download exposed invoice PDF
         * @param {string} invoiceId The invoice\&#39;s unique identifier
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadPortalInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadPortalInvoicePdf(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of invoices that a practitioner has exposed to the authenticated client in the portal. Invoices are ordered by invoice date descending (most recent first).  An invoice is exposed by exposing its stored PDF in the client\'s dossier, so an invoice with no stored document is never listed. Only issued invoices are returned; cancelled (credited) ones leave the list by the same rule. 
         * @summary List exposed invoices
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPortalInvoices(page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<PortalInvoiceListResponse> {
            return localVarFp.listPortalInvoices(page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PortalInvoicesApi - object-oriented interface
 */
export class PortalInvoicesApi extends BaseAPI {
    /**
     * Streams the stored PDF of an invoice that has been exposed to the authenticated client — the document they were sent, not a fresh render.  Cross-client / non-exposed access returns 404 to avoid leaking the existence of invoices that don\'t belong to the calling client. An invoice that is not issued, or has no stored document, returns 404 for the same reason. 
     * @summary Download exposed invoice PDF
     * @param {string} invoiceId The invoice\&#39;s unique identifier
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadPortalInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig) {
        return PortalInvoicesApiFp(this.configuration).downloadPortalInvoicePdf(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of invoices that a practitioner has exposed to the authenticated client in the portal. Invoices are ordered by invoice date descending (most recent first).  An invoice is exposed by exposing its stored PDF in the client\'s dossier, so an invoice with no stored document is never listed. Only issued invoices are returned; cancelled (credited) ones leave the list by the same rule. 
     * @summary List exposed invoices
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listPortalInvoices(page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return PortalInvoicesApiFp(this.configuration).listPortalInvoices(page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



